import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export const MEMO_COMMENT_DRAFT_HIGHLIGHT_ID = '__memo-comment-draft__';

export type MemoCommentHighlightRange = {
    commentId: string;
    from: number;
    to: number;
};

type MemoCommentHighlightMeta =
    | {
          type: 'clear';
      }
    | {
          type: 'clearDraft';
      }
    | {
          commentId: string;
          type: 'remove';
      }
    | {
          from: number;
          to: number;
          type: 'setDraft';
      }
    | {
          from: number;
          to: number;
          commentId: string;
          type: 'add';
      }
    | {
          highlights: MemoCommentHighlightRange[];
          type: 'replace';
      };

const commentHighlightAttributes = (commentId: string) => {
    const isDraft = commentId === MEMO_COMMENT_DRAFT_HIGHLIGHT_ID;

    return {
        class: [
            'memo-comment-selection-highlight',
            isDraft ? 'memo-comment-selection-highlight-draft' : 'memo-comment-selection-highlight-comment',
        ].join(' '),
        ...(isDraft
            ? {}
            : {
                  'data-memo-comment-id': commentId,
              }),
    };
};

const createHighlightDecoration = ({ commentId, from, to }: MemoCommentHighlightRange) =>
    Decoration.inline(from, to, commentHighlightAttributes(commentId), { commentId });

const validHighlight = ({ from, to }: MemoCommentHighlightRange) =>
    typeof from === 'number' && typeof to === 'number' && Number.isFinite(from) && Number.isFinite(to) && from < to;

export const memoCommentHighlightPluginKey = new PluginKey<DecorationSet>('memoCommentHighlight');

export const MemoCommentHighlight = Extension.create({
    name: 'memoCommentHighlight',

    addProseMirrorPlugins() {
        return [
            new Plugin<DecorationSet>({
                key: memoCommentHighlightPluginKey,
                state: {
                    init: () => DecorationSet.empty,
                    apply(transaction, decorations, _oldState, newState) {
                        const meta = transaction.getMeta(memoCommentHighlightPluginKey) as
                            | MemoCommentHighlightMeta
                            | undefined;
                        const mappedDecorations = decorations.map(transaction.mapping, transaction.doc);

                        if (meta?.type === 'clear') {
                            return DecorationSet.empty;
                        }

                        if (meta?.type === 'replace') {
                            return DecorationSet.create(
                                newState.doc,
                                meta.highlights.filter(validHighlight).map(createHighlightDecoration),
                            );
                        }

                        if (meta?.type === 'clearDraft') {
                            return mappedDecorations.remove(
                                mappedDecorations.find(
                                    undefined,
                                    undefined,
                                    (spec) => spec.commentId === MEMO_COMMENT_DRAFT_HIGHLIGHT_ID,
                                ),
                            );
                        }

                        if (meta?.type === 'remove') {
                            return mappedDecorations.remove(
                                mappedDecorations.find(
                                    undefined,
                                    undefined,
                                    (spec) => spec.commentId === meta.commentId,
                                ),
                            );
                        }

                        const nextHighlight =
                            meta?.type === 'setDraft'
                                ? {
                                      commentId: MEMO_COMMENT_DRAFT_HIGHLIGHT_ID,
                                      from: meta.from,
                                      to: meta.to,
                                  }
                                : meta?.type === 'add'
                                  ? {
                                        commentId: meta.commentId,
                                        from: meta.from,
                                        to: meta.to,
                                    }
                                  : undefined;

                        if (nextHighlight && validHighlight(nextHighlight)) {
                            const withoutExisting = mappedDecorations.remove(
                                mappedDecorations.find(
                                    undefined,
                                    undefined,
                                    (spec) => spec.commentId === nextHighlight.commentId,
                                ),
                            );

                            return withoutExisting.add(newState.doc, [createHighlightDecoration(nextHighlight)]);
                        }

                        return mappedDecorations;
                    },
                },
                props: {
                    decorations(state) {
                        return memoCommentHighlightPluginKey.getState(state);
                    },
                },
            }),
        ];
    },
});
